import { proxy, useSnapshot } from 'valtio';
import { config } from '@/app/config';
import posthog from "posthog-js";
import { ErrorReporter } from './tracking';


// Create a proxy state that can be imported and modified from anywhere
export const sessionState = proxy({
  user: null,
  token: null,
  cardId: null,
  channel: null,
  endpointId: null,
  sub: null,
  isLoading: true,  
  demoData: {
    enabled: false,
    data: "{}",
    dataUrl: ""
  }
});

// Function to refresh session data using the current token
export const refreshSession = async () => {
  if (!sessionState.token) {
    console.warn('No token available to refresh session');
    sessionState.isLoading = false;
    return;
  }

   let url = "";
  try {
    sessionState.isLoading = true;
    url = `${config.API_URL}/api/session/myprofile?signIn=true&sub=${sessionState.sub || ''}`;
    const fetchOptions = {
      headers: {
        'Authorization': `Bearer ${sessionState.token}`,
      },
    };
    
    /*
    // Include cookies when sub has a value
    if (sessionState.sub) {
      fetchOptions.credentials = 'include';
    }
    */

    fetchOptions.credentials = 'include';  // we always need to include cookies to allow signin for cookie authentication
    
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      // Report unexpected authentication failures for monitoring
      if (response.status === 401) {
        ErrorReporter.error(`Unexpected 401 from /api/session/myprofile - Status: ${response.status}, Token: ${sessionState?.token ? 'present' : 'missing'}, Sub: ${sessionState?.sub || 'none'}`);
        sessionState.token = null;
        sessionState.user = null;
        sessionState.sub = null;
      } else {
        ErrorReporter.error(`Session refresh failed - Status: ${response.status} ${response.statusText}, URL: ${url}`);
      }
      throw new Error(`Failed to load session: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (data.ErrorCode) {
      // Report API-level authentication errors
      if (data.ErrorCode === 401 || data.ErrorCode === 403) {
        ErrorReporter.error(`Unexpected auth error from /api/session/myprofile - ErrorCode: ${data.ErrorCode}, ErrorText: ${data.ErrorText}, Token: ${sessionState.token ? 'present' : 'missing'}`);
        sessionState.token = null;
        sessionState.user = null;
        sessionState.sub = null;
      } else {
        ErrorReporter.error(`Session refresh API error - ErrorCode: ${data.ErrorCode}, ErrorText: ${data.ErrorText}`);
      }
      throw new Error('Failed to load session with error code: ' + data.ErrorCode);
    }

    if (posthog && data.Data?.Email) {
      posthog.identify(
        data.Data?.Email,
        {
          email: data.Data?.Email,
          userAgent: navigator?.userAgent,
        }
      );
    }
    

    // Update the sessionState with user data
    Object.assign(sessionState, data.Data);
  } catch (error) {
    console.error('Error loading session:', error);
    // Report session refresh failures for monitoring
    ErrorReporter.error(`Session refresh failed: ${error.message}, Token: ${sessionState.token ? 'present' : 'missing'}, URL: ${url || 'unknown'} Token: ${sessionState.token}`);
  } finally {
    sessionState.isLoading = false;
  }
};

// Function to update token and refresh session
export const updateToken = (newToken, newCardId = null, sub = null, channel = null, endpointId = null) => {
  sessionState.token = newToken;
  sessionState.sub = sub; 
  sessionState.channel = channel;
  sessionState.endpointId = endpointId;

  if (newCardId !== null) {
    sessionState.cardId = newCardId;
  }

  // Refresh session with new token
  return refreshSession();
};

// Utility hook for components that need to access session state
export function useSession() {
  return useSnapshot(sessionState);
}